import React from "react";
import PropTypes from "prop-types";
import { ChevronLeft, Close, IkonateContext } from "react-ikonate";
import PersonSelect from "../PersonSelect";
import styles from "./IboHeader.module.css";

const PIN_LABELS = {
  IBO: "IBO",
  PLA: "Platinum",
  EME: "Emerald",
  DIA: "Diamond",
};
const MEMBERSHIP_LABELS = { C: "Customer", P: "Prospect" };

// Shared "top part" for both Order History and Create Order: the IBO identity
// block (number, name, person selector) on the left and the IBO meta details
// (pin level, membership, contact) on the right. Only the content below it
// differs between the two tabs.
export function IboHeader({
  selectedIbo,
  people,
  selectedPersonId,
  onPersonChange,
  onBack,
  backLabel,
  onClear,
}) {
  if (!selectedIbo) return null;

  // Contact details follow whoever the rep is acting as (David Nelson's
  // per-contact query), falling back to the account's.
  const person = (people || []).find(
    (p) => String(p?.id) === String(selectedPersonId)
  );
  const email = person?.email || selectedIbo.email;
  const phone = person?.phone || selectedIbo.phone;

  return (
    <div className={styles.header}>
      {onBack && (
        <button
          type="button"
          className={styles.backButton}
          title={`Go back to ${backLabel}.`}
          onClick={onBack}
        >
          <ChevronLeft size={18} />
          <span className={styles.backText}>{backLabel}</span>
        </button>
      )}

      <section className={styles.selectionHeader}>
        <div className={styles.selectionHeaderMain}>
          <div className={styles.iboNumberRow}>
            <p className={styles.iboNumber}>IBO {selectedIbo.iboNumber}</p>
            {onClear && (
              <button
                type="button"
                className={styles.clearIbo}
                onClick={onClear}
                aria-label="Clear IBO"
                title="Clear this IBO and go back to the IBO search."
              >
                <IkonateContext.Provider
                  value={{ size: 14, stroke: 2.5, color: "currentColor" }}
                >
                  <Close />
                </IkonateContext.Provider>
              </button>
            )}
          </div>
          <h1 className={styles.iboName}>
            {selectedIbo.displayName || selectedIbo.name}
          </h1>
          <PersonSelect
            people={people}
            value={selectedPersonId}
            onChange={onPersonChange}
            name={`ibo-person-${selectedIbo.iboNumber}`}
          />
        </div>

        <div className={styles.iboMeta}>
          <div className={styles.iboMetaRow}>
            <span className={styles.iboMetaLabel}>Pin Level</span>
            <span className={styles.iboMetaValue}>
              {PIN_LABELS[selectedIbo.pinLevel] ||
                selectedIbo.pinLevel ||
                "IBO"}
            </span>
          </div>
          <div className={styles.iboMetaRow}>
            <span className={styles.iboMetaLabel}>Membership</span>
            <span className={styles.iboMetaValue}>
              {MEMBERSHIP_LABELS[selectedIbo.membership] ||
                selectedIbo.membership ||
                "—"}
            </span>
          </div>
          {email && (
            <div className={styles.iboMetaRow}>
              <span className={styles.iboMetaLabel}>Email</span>
              <span className={styles.iboMetaValue}>{email}</span>
            </div>
          )}
          {phone && (
            <div className={styles.iboMetaRow}>
              <span className={styles.iboMetaLabel}>Phone</span>
              <span className={styles.iboMetaValue}>{phone}</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

IboHeader.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedIbo: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  people: PropTypes.array,
  selectedPersonId: PropTypes.string,
  onPersonChange: PropTypes.func,
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  onClear: PropTypes.func,
};

IboHeader.defaultProps = {
  selectedIbo: null,
  people: [],
  selectedPersonId: "",
  onPersonChange: undefined,
  onBack: undefined,
  backLabel: "IBO Lookup",
  onClear: undefined,
};

export default IboHeader;
